import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type LedgerStatementDownloadStatus = 'opened' | 'downloaded';

export interface LedgerStatementDownloadOptions {
  distributorId: string;
  startDate?: Date;
  endDate?: Date;
  fileName?: string;
  cleanupDelayMs?: number;
}

export interface LedgerStatementDownloadResult {
  status: LedgerStatementDownloadStatus;
}

// Same YYYY-MM-DD-in-local-timezone convention as the ledger-statement page's own formatDate.
function formatDate(date: Date) {
  return date.toLocaleDateString('en-CA');
}

@Injectable()
export class LedgerStatementDownloadService {
  private static readonly DEFAULT_CLEANUP_DELAY_MS = 1000;

  constructor(private readonly http: HttpClient) {}

  async openStatement({
    distributorId,
    startDate,
    endDate,
    fileName,
    cleanupDelayMs,
  }: LedgerStatementDownloadOptions): Promise<LedgerStatementDownloadResult> {
    if (!distributorId) {
      throw new Error('A distributorId is required to generate a ledger statement.');
    }

    const params: Record<string, string> = {};
    if (startDate) params['startDate'] = formatDate(startDate);
    if (endDate) params['endDate'] = formatDate(endDate);

    const pdfBlob = await firstValueFrom(
      this.http.get(`/api/distributors/${distributorId}/ledger-statement/pdf`, {
        responseType: 'blob',
        params,
      })
    );

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      // Non-browser environment (SSR) – just return a successful response.
      return { status: 'downloaded' };
    }

    const blobUrl = window.URL.createObjectURL(pdfBlob);
    const revokeDelay = cleanupDelayMs ?? LedgerStatementDownloadService.DEFAULT_CLEANUP_DELAY_MS;
    const revokeBlob = () => window.URL.revokeObjectURL(blobUrl);

    const newTab = window.open(blobUrl, '_blank');

    if (!newTab) {
      // Pop-up blocked – trigger download instead.
      const downloadName = fileName ?? `ledger-statement_${distributorId}.pdf`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        document.body.removeChild(link);
        setTimeout(revokeBlob, revokeDelay);
      }

      return { status: 'downloaded' };
    }

    await new Promise(resolve => setTimeout(resolve, 100));
    if (newTab.closed) {
      setTimeout(revokeBlob, revokeDelay);
      return { status: 'downloaded' };
    }

    setTimeout(revokeBlob, revokeDelay);
    return { status: 'opened' };
  }
}
